import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic2 } from "lucide-react";
import { api } from "../../lib/api";
import { SkeletonCardList } from "../../components/skeleton";
import { EmptyState } from "../../components/empty-state";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";

function Mic2Icon() {
  return <Mic2 className="h-6 w-6" />;
}

interface VoiceProfile {
  id: string;
  name: string;
  status: "pending" | "training" | "ready" | "failed";
  source_count: number;
  total_chars: number;
  quality_score: number;
  quality_rating: "weak" | "fair" | "good" | "strong";
  created_at: number;
  updated_at: number;
}

export default function VoicesPage() {
  const [voices, setVoices] = useState<VoiceProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    void load(true);
    const t = setInterval(() => void load(false), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(initial: boolean) {
    try {
      const res = await api.get<{ voice_profiles: VoiceProfile[] }>(
        "/api/voice-profiles"
      );
      setVoices(res.voice_profiles);
    } catch (err) {
      if (initial) toast.error(apiErrorMessage(err));
    } finally {
      if (initial) setLoading(false);
    }
  }

  return (
    <div>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title">Voice profiles</h1>
          <p className="page-subtitle">
            Train a profile from your past content. Generation will copy your
            tone, structure, and rhythm.
          </p>
        </div>
        <Link to="/app/voices/new" className="btn-brand no-underline">
          + New voice
        </Link>
      </div>

      {loading ? (
        <SkeletonCardList count={2} />
      ) : voices.length === 0 ? (
        <EmptyState
          icon={<Mic2Icon />}
          title="Train your first voice"
          description="Paste 3–10 of your past posts. BrandEcho extracts the structural style — tone, sentence rhythm, openers — and uses it to ground every future generation."
          action={
            <Link to="/app/voices/new" className="btn-brand no-underline">
              Start training
            </Link>
          }
        />
      ) : (
        <ul className="grid gap-3">
          {voices.map((v) => (
            <li key={v.id}>
              <Link
                to={`/app/voices/${v.id}`}
                className="flex items-center justify-between gap-4 rounded-xl border border-ink-200/70 bg-white p-5 shadow-xs no-underline transition-all hover:-translate-y-0.5 hover:border-ink-200 hover:shadow-md"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-ink-900">
                    {v.name}
                  </div>
                  <div className="text-xs text-ink-500">
                    {new Date(v.created_at).toLocaleString()} · {v.source_count}{" "}
                    {v.source_count === 1 ? "sample" : "samples"}
                  </div>
                  {v.status === "ready" || v.source_count > 0 ? (
                    <QualityBar score={v.quality_score} rating={v.quality_rating} />
                  ) : null}
                </div>
                <StatusBadge status={v.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QualityBar({
  score,
  rating,
}: {
  score: number;
  rating: VoiceProfile["quality_rating"];
}) {
  const colorMap: Record<VoiceProfile["quality_rating"], string> = {
    weak: "bg-red-500",
    fair: "bg-yellow-500",
    good: "bg-blue-500",
    strong: "bg-emerald-500",
  };
  const labelMap: Record<VoiceProfile["quality_rating"], string> = {
    weak: "Weak",
    fair: "Fair",
    good: "Good",
    strong: "Strong",
  };
  return (
    <div className="mt-2 flex items-center gap-2 max-w-xs">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-200">
        <div
          className={`h-1.5 transition-all ${colorMap[rating]}`}
          style={{ width: `${Math.max(2, score)}%` }}
        />
      </div>
      <span className="whitespace-nowrap text-xs text-ink-500">
        {score}/100 · {labelMap[rating]}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: VoiceProfile["status"] }) {
  const map: Record<VoiceProfile["status"], string> = {
    pending: "bg-ink-100 text-ink-700",
    training: "bg-amber-100 text-amber-700",
    ready: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700",
  };
  return (
    <span className={`whitespace-nowrap rounded px-2 py-1 text-xs ${map[status]}`}>
      {status}
    </span>
  );
}
